import { useEffect, useState } from 'react';
import { Ban, Search, ShieldCheck } from 'lucide-react';
import { isBanActive, isIndefiniteBan } from '@app/common';
import type { AdminUserSummary } from '@app/common';
import { useAdminUsers, ADMIN_PAGE_SIZE } from '../../hooks/useAdminData';
import { useTranslation } from '../../context/LanguageContext';
import { PLAN_NAME_KEYS } from '../../i18n';
import { AdminPanel, AdminStateRow, Pager, formatDate, formatNumber } from './AdminShared';
import { BanUserDialog } from './BanUserDialog';
import { unbanUser } from '../../api/admin.api';

export function AdminUsersTab({
  onViewActivity,
}: Readonly<{ onViewActivity: (userId: string) => void }>) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [banTarget, setBanTarget] = useState<AdminUserSummary | null>(null);
  // Which row is mid-unban, so its button can disable itself without freezing the table.
  const [unbanning, setUnbanning] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const { data, loading, error, reload } = useAdminUsers(page, search);
  const users = data?.items ?? [];

  const handleUnban = async (user: AdminUserSummary) => {
    setUnbanning(user.id);
    setActionError(null);
    try {
      await unbanUser(user.id);
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('admin.unbanFailed'));
    } finally {
      setUnbanning(null);
    }
  };

  return (
    <AdminPanel
      title={t('nav.adminUsers')}
      actions={
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500 pointer-events-none" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('admin.searchUsers')}
            className="w-56 rounded-lg border border-slate-700 bg-slate-900 py-1.5 pl-8 pr-3 text-xs text-slate-200 placeholder:text-slate-500 focus:border-violet-500 focus:outline-none"
          />
        </div>
      }
    >
      <AdminStateRow
        loading={loading}
        error={error}
        empty={users.length === 0}
        emptyLabel={t('admin.noUsers')}
      />

      {/* A failed unban is reported above the table rather than in the row: the row
          it applies to may have moved once the list reloads. */}
      {actionError && (
        <p className="px-5 pt-3 text-xs text-rose-400" role="alert">
          {actionError}
        </p>
      )}

      {banTarget && (
        <BanUserDialog user={banTarget} onClose={() => setBanTarget(null)} onBanned={reload} />
      )}

      {!loading && !error && users.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-700 text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-2.5 font-bold">{t('admin.colUser')}</th>
                  <th className="px-3 py-2.5 font-bold">{t('admin.colPlan')}</th>
                  <th className="px-3 py-2.5 font-bold">{t('admin.colSignedUp')}</th>
                  <th className="px-3 py-2.5 font-bold">{t('admin.colLastSeen')}</th>
                  <th className="px-3 py-2.5 font-bold text-right">{t('admin.colGenerations')}</th>
                  <th className="px-3 py-2.5 font-bold">{t('admin.colAccount')}</th>
                  <th className="px-5 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {users.map((u) => {
                  const banned = isBanActive(u.bannedUntil);
                  return (
                    <tr
                      key={u.id}
                      className={`transition-colors ${banned ? 'bg-rose-500/5 hover:bg-rose-500/10' : 'hover:bg-slate-700/40'}`}
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-white truncate flex items-center gap-1.5">
                              {u.displayName ?? u.email ?? u.id.slice(0, 8)}
                              {u.role === 'superadmin' && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/20 px-1.5 py-0.5 text-[10px] font-bold text-violet-300">
                                  <ShieldCheck className="h-2.5 w-2.5" />
                                  {t('admin.roleBadge')}
                                </span>
                              )}
                            </p>
                            <p className="text-[11px] text-slate-500 truncate">{u.email ?? '—'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {u.plan ? (
                          <span className="text-xs text-slate-300">
                            {t(PLAN_NAME_KEYS[u.plan])}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-500">—</span>
                        )}
                        {u.status && (
                          <p className="text-[10px] text-slate-500">{t(`status.${u.status}`)}</p>
                        )}
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-400 whitespace-nowrap">
                        {formatDate(u.createdAt)}
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-400 whitespace-nowrap">
                        {u.lastSignInAt ? formatDate(u.lastSignInAt) : t('admin.never')}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <span className="text-xs font-bold text-violet-400">
                          {formatNumber(u.totalGenerations)}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        {banned ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/20 px-1.5 py-0.5 text-[10px] font-bold text-rose-300">
                            <Ban className="h-2.5 w-2.5" />
                            {t('admin.bannedBadge')}
                          </span>
                        ) : (
                          <span className="text-[11px] text-slate-500">{t('admin.accountOk')}</span>
                        )}
                        {banned && u.bannedUntil && (
                          <p className="mt-0.5 text-[10px] text-slate-500">
                            {isIndefiniteBan(u.bannedUntil)
                              ? t('admin.banIndefinite')
                              : t('admin.bannedUntil', { date: formatDate(u.bannedUntil) })}
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            type="button"
                            onClick={() => onViewActivity(u.id)}
                            className="text-xs font-semibold text-violet-400 hover:text-violet-300 whitespace-nowrap transition-colors"
                          >
                            {t('admin.viewActivity')}
                          </button>
                          {/* Superadmins are not offered the control at all — the API
                            refuses to moderate a peer, so a button here would only
                            ever produce an error. */}
                          {u.role !== 'superadmin' &&
                            (banned ? (
                              <button
                                type="button"
                                disabled={unbanning === u.id}
                                onClick={() => void handleUnban(u)}
                                className="text-xs font-semibold text-emerald-400 hover:text-emerald-300 whitespace-nowrap transition-colors disabled:opacity-40"
                              >
                                {t('admin.unban')}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setBanTarget(u)}
                                className="text-xs font-semibold text-rose-400 hover:text-rose-300 whitespace-nowrap transition-colors"
                              >
                                {t('admin.ban')}
                              </button>
                            ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager
            page={page}
            pageSize={ADMIN_PAGE_SIZE}
            total={data?.total ?? 0}
            onChange={setPage}
          />
        </>
      )}
    </AdminPanel>
  );
}
